import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from './ui/Button';
import { theme } from '../styles/design-system';
import {
    MessageSquare,
    Zap,
    TrendingUp,
    Mic,
    Brain,
    DollarSign,
    User,
    ArrowRight,
    Sparkles,
    BarChart3,
    Bell,
    Settings,
    ChevronRight,
    CheckCircle2
} from 'lucide-react';

const LandingPage = () => {
    const [hoveredCard, setHoveredCard] = useState(null);

    const features = [
        {
            path: '/chat',
            icon: <MessageSquare size={32} />,
            title: 'AI Chat Assistant',
            tagline: 'Your intelligent trading companion',
            description: 'Engage with our advanced AI assistant for real-time market insights, strategy discussions, and personalized trading advice. Get instant answers to your trading questions.',
            benefits: [
                'Real-time market analysis',
                'Strategy recommendations',
                '24/7 intelligent support',
                'Natural language queries'
            ],
            gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#667eea'
        },
        {
            path: '/algo-runner',
            icon: <Zap size={32} />,
            title: 'Algo Runner (Equity)',
            tagline: 'Automated equity algorithmic trading',
            description: 'Execute sophisticated algorithmic trading strategies for EQUITY markets with built-in backtesting, optimization, and live deployment. Run multiple equity strategies simultaneously with full control and real-time monitoring.',
            benefits: [
                'Equity-focused algorithms',
                'Automated strategy execution',
                'Real-time backtesting',
                'Performance analytics'
            ],
            gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: '#f5576c'
        },
        {
            path: '/fno-trading',
            icon: <TrendingUp size={32} />,
            title: 'F&O Algo Trading',
            tagline: 'Advanced algorithmic derivatives trading',
            description: 'Algorithmic trading system specifically designed for FUTURES & OPTIONS markets. Combine ancient numerology wisdom with modern technology for optimal trade entries in derivatives.',
            benefits: [
                'F&O-specific algorithms',
                'Numerology-based signals',
                'Muhurat timing analysis',
                'Options chain analysis'
            ],
            gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: '#00f2fe'
        },
        {
            path: '/voice-alerts',
            icon: <Mic size={32} />,
            title: 'Voice Alerts',
            tagline: 'Never miss a trading opportunity',
            description: 'Configure intelligent voice notifications for market events, price targets, and strategy triggers. Stay informed even when away from your screen.',
            benefits: [
                'Custom alert conditions',
                'Voice notifications',
                'Multi-channel delivery',
                'Smart trigger logic'
            ],
            gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            color: '#fa709a'
        },
        {
            path: '/ai-dashboard',
            icon: <Brain size={32} />,
            title: 'AI Dashboard',
            tagline: 'Intelligence at your fingertips',
            description: 'Monitor AI agent performance, view recommendations, and track optimization metrics. Your central hub for all AI-powered trading intelligence.',
            benefits: [
                'Agent performance monitoring',
                'AI recommendations',
                'Optimization history',
                'System intelligence metrics'
            ],
            gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            color: '#a8edea'
        },
        {
            path: '/revenue',
            icon: <DollarSign size={32} />,
            title: 'Revenue Analytics',
            tagline: 'Track your trading success',
            description: 'Comprehensive revenue tracking, P&L analysis, and financial health monitoring. Understand your trading performance with deep analytics.',
            benefits: [
                'P&L tracking',
                'Revenue streams analysis',
                'Performance metrics',
                'Tax optimization insights'
            ],
            gradient: 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)',
            color: '#fbc2eb'
        }
    ];

    const stats = [
        { label: 'Active Features', value: '6+', icon: <Sparkles size={20} /> },
        { label: 'AI Agents', value: '5+', icon: <Brain size={20} /> },
        { label: 'Trading Strategies', value: '10+', icon: <BarChart3 size={20} /> },
        { label: 'Alert Types', value: '15+', icon: <Bell size={20} /> }
    ];

    return (
        <div style={styles.container}>
            {/* Hero Section */}
            <section style={styles.heroSection}>
                <div style={styles.heroContent}>
                    <div style={styles.badge}>
                        <Sparkles size={16} />
                        <span>AI-Powered Trading Platform</span>
                    </div>
                    <h1 style={styles.heroTitle}>
                        Welcome to <span style={styles.brandHighlight}>AstroTrader AI</span>
                    </h1>
                    <p style={styles.heroSubtitle}>
                        Experience the future of algorithmic trading with AI-powered insights,
                        automated strategies, and intelligent market analysis. Your complete
                        trading ecosystem in one powerful platform.
                    </p>
                    <div style={styles.heroActions}>
                        <Link to="/algo-runner" style={{ textDecoration: 'none' }}>
                            <Button
                                variant="primary"
                                size="lg"
                                icon={<Zap size={20} />}
                                style={styles.primaryButton}
                            >
                                Start Trading
                            </Button>
                        </Link>
                        <Link to="/profile" style={{ textDecoration: 'none' }}>
                            <Button
                                variant="outline"
                                size="lg"
                                icon={<User size={20} />}
                                style={styles.secondaryButton}
                            >
                                View Profile
                            </Button>
                        </Link>
                    </div>

                    {/* Video Showcase */}
                    <div style={styles.videoWrapper}>
                        <video
                            controls
                            autoPlay
                            muted
                            loop
                            style={styles.heroVideo}
                        >
                            <source src="https://storage.googleapis.com/satsangpublicurls/Astro%20Trading.mp4" type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    </div>
                </div>

                {/* Stats Row */}
                <div style={styles.statsContainer}>
                    {stats.map((stat, idx) => (
                        <div key={idx} style={styles.statCard}>
                            <div style={styles.statIcon}>{stat.icon}</div>
                            <div style={styles.statValue}>{stat.value}</div>
                            <div style={styles.statLabel}>{stat.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Features Section */}
            <section style={styles.featuresSection}>
                <div style={styles.sectionHeader}>
                    <h2 style={styles.sectionTitle}>Powerful Features, Simplified</h2>
                    <p style={styles.sectionSubtitle}>
                        Explore our comprehensive suite of trading tools designed to give you an edge in the markets
                    </p>
                </div>

                <div style={styles.featuresGrid}>
                    {features.map((feature, index) => (
                        <div
                            key={feature.path}
                            style={{
                                ...styles.featureCard,
                                transform: hoveredCard === index ? 'translateY(-8px)' : 'translateY(0)',
                                boxShadow: hoveredCard === index
                                    ? '0 20px 40px rgba(0,0,0,0.15)'
                                    : '0 4px 6px rgba(0,0,0,0.1)',
                            }}
                            onMouseEnter={() => setHoveredCard(index)}
                            onMouseLeave={() => setHoveredCard(null)}
                        >
                            {/* Card Header with Gradient */}
                            <div style={{
                                ...styles.featureHeader,
                                background: feature.gradient
                            }}>
                                <div style={styles.featureIcon}>
                                    {feature.icon}
                                </div>
                            </div>

                            {/* Card Content */}
                            <div style={styles.featureContent}>
                                <h3 style={styles.featureTitle}>{feature.title}</h3>
                                <p style={styles.featureTagline}>{feature.tagline}</p>
                                <p style={styles.featureDescription}>{feature.description}</p>

                                {/* Benefits List */}
                                <div style={styles.benefitsList}>
                                    {feature.benefits.map((benefit, idx) => (
                                        <div key={idx} style={styles.benefitItem}>
                                            <CheckCircle2 size={16} color={feature.color} />
                                            <span>{benefit}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* CTA Button */}
                                <Link to={feature.path} style={{ textDecoration: 'none', width: '100%' }}>
                                    <Button
                                        variant="outline"
                                        style={{
                                            ...styles.featureButton,
                                            borderColor: feature.color,
                                            color: feature.color
                                        }}
                                        icon={<ArrowRight size={16} />}
                                    >
                                        Explore {feature.title}
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    ))}
                </div>
            </section >

            {/* How It Works Section */}
            < section style={styles.howItWorksSection} >
                <div style={styles.sectionHeader}>
                    <h2 style={styles.sectionTitle}>How It Works</h2>
                    <p style={styles.sectionSubtitle}>Get started in minutes with our simple three-step process</p>
                </div>

                <div style={styles.stepsContainer}>
                    <div style={styles.stepCard}>
                        <div style={styles.stepNumber}>1</div>
                        <div style={styles.stepIcon}>
                            <User size={32} color="#667eea" />
                        </div>
                        <h3 style={styles.stepTitle}>Set Up Your Profile</h3>
                        <p style={styles.stepDescription}>
                            Configure your trading preferences, risk parameters, and API connections
                        </p>
                    </div>

                    <div style={styles.stepArrow}>
                        <ChevronRight size={32} color="var(--text-tertiary)" />
                    </div>

                    <div style={styles.stepCard}>
                        <div style={styles.stepNumber}>2</div>
                        <div style={styles.stepIcon}>
                            <Settings size={32} color="#f5576c" />
                        </div>
                        <h3 style={styles.stepTitle}>Choose Your Strategy</h3>
                        <p style={styles.stepDescription}>
                            Select from pre-built strategies or create custom algorithms tailored to your goals
                        </p>
                    </div>

                    <div style={styles.stepArrow}>
                        <ChevronRight size={32} color="var(--text-tertiary)" />
                    </div>

                    <div style={styles.stepCard}>
                        <div style={styles.stepNumber}>3</div>
                        <div style={styles.stepIcon}>
                            <Zap size={32} color="#00f2fe" />
                        </div>
                        <h3 style={styles.stepTitle}>Start Trading</h3>
                        <p style={styles.stepDescription}>
                            Launch your strategies and let AI-powered automation handle the rest
                        </p>
                    </div>
                </div>
            </section >

            {/* CTA Section */}
            < section style={styles.ctaSection} >
                <h2 style={styles.ctaTitle}>Ready to Transform Your Trading?</h2>
                <p style={styles.ctaSubtitle}>
                    Join thousands of traders leveraging AI to make smarter trading decisions
                </p>
                <Link to="/algo-runner" style={{ textDecoration: 'none' }}>
                    <Button
                        variant="primary"
                        size="lg"
                        icon={<Zap size={20} />}
                        style={styles.ctaButton}
                    >
                        Get Started Now
                    </Button>
                </Link>
            </section >
        </div >
    );
};

const styles = {
    container: {
        fontFamily: theme.typography.fontFamily.sans.join(', '),
        backgroundColor: 'var(--bg-secondary)',
        minHeight: '100vh',
        paddingBottom: '80px', // Space for bottom nav
    },

    // Hero Section
    heroSection: {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: `${theme.spacing[16]} ${theme.spacing[6]}`,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
    },
    heroContent: {
        maxWidth: '900px',
        margin: '0 auto',
        textAlign: 'center',
        position: 'relative',
        zIndex: 1,
    },
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing[2],
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        backdropFilter: 'blur(10px)',
        padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
        borderRadius: '999px',
        fontSize: theme.typography.fontSize.sm,
        fontWeight: theme.typography.fontWeight.semibold,
        marginBottom: theme.spacing[6],
        border: '1px solid rgba(255, 255, 255, 0.3)',
    },
    heroTitle: {
        fontSize: theme.typography.fontSize['5xl'],
        fontWeight: theme.typography.fontWeight.extrabold,
        marginBottom: theme.spacing[6],
        letterSpacing: '-0.02em',
        lineHeight: 1.1,
    },
    brandHighlight: {
        background: 'linear-gradient(to right, #fbbf24, #f59e0b)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
    },
    heroSubtitle: {
        fontSize: theme.typography.fontSize.xl,
        opacity: 0.95,
        marginBottom: theme.spacing[8],
        lineHeight: 1.6,
        maxWidth: '700px',
        margin: '0 auto',
    },
    heroActions: {
        display: 'flex',
        gap: theme.spacing[4],
        justifyContent: 'center',
        flexWrap: 'wrap',
        marginBottom: theme.spacing[12],
    },
    primaryButton: {
        backgroundColor: 'white',
        color: '#667eea',
        fontWeight: theme.typography.fontWeight.bold,
    },
    secondaryButton: {
        borderColor: 'rgba(255, 255, 255, 0.5)',
        color: 'white',
    },
    videoWrapper: {
        marginTop: theme.spacing[8],
        borderRadius: theme.borderRadius['2xl'],
        overflow: 'hidden',
        boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
        border: '2px solid rgba(255, 255, 255, 0.3)',
        width: '100%',
        maxWidth: '1200px',
        margin: `${theme.spacing[8]} auto 0`,
        position: 'relative',
        zIndex: 2,
    },
    heroVideo: {
        width: '100%',
        height: 'auto',
        minHeight: '500px',
        display: 'block',
        objectFit: 'contain',
        backgroundColor: '#000',
    },
    statsContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: theme.spacing[4],
        maxWidth: '1000px',
        margin: '0 auto',
    },
    statCard: {
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        backdropFilter: 'blur(10px)',
        padding: theme.spacing[6],
        borderRadius: theme.borderRadius.xl,
        textAlign: 'center',
        border: '1px solid rgba(255, 255, 255, 0.2)',
    },
    statIcon: {
        marginBottom: theme.spacing[2],
        opacity: 0.9,
    },
    statValue: {
        fontSize: theme.typography.fontSize['3xl'],
        fontWeight: theme.typography.fontWeight.bold,
        marginBottom: theme.spacing[1],
    },
    statLabel: {
        fontSize: theme.typography.fontSize.sm,
        opacity: 0.9,
        fontWeight: theme.typography.fontWeight.medium,
    },

    // Features Section
    featuresSection: {
        padding: `${theme.spacing[16]} ${theme.spacing[6]}`,
        maxWidth: '1400px',
        margin: '0 auto',
    },
    sectionHeader: {
        textAlign: 'center',
        marginBottom: theme.spacing[12],
    },
    sectionTitle: {
        fontSize: theme.typography.fontSize['4xl'],
        fontWeight: theme.typography.fontWeight.bold,
        color: 'var(--text-primary)',
        marginBottom: theme.spacing[4],
        letterSpacing: '-0.02em',
    },
    sectionSubtitle: {
        fontSize: theme.typography.fontSize.lg,
        color: 'var(--text-secondary)',
        maxWidth: '600px',
        margin: '0 auto',
    },
    featuresGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
        gap: theme.spacing[8],
    },
    featureCard: {
        backgroundColor: 'var(--bg-primary)',
        borderRadius: theme.borderRadius['2xl'],
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        border: '1px solid var(--border-primary)',
        display: 'flex',
        flexDirection: 'column',
    },
    featureHeader: {
        padding: theme.spacing[8],
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    featureIcon: {
        color: 'white',
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        padding: theme.spacing[4],
        borderRadius: theme.borderRadius.xl,
        backdropFilter: 'blur(10px)',
    },
    featureContent: {
        padding: theme.spacing[6],
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing[3],
        flex: 1,
    },
    featureTitle: {
        fontSize: theme.typography.fontSize['2xl'],
        fontWeight: theme.typography.fontWeight.bold,
        color: 'var(--text-primary)',
        marginBottom: 0,
    },
    featureTagline: {
        fontSize: theme.typography.fontSize.sm,
        color: 'var(--text-tertiary)',
        fontStyle: 'italic',
        marginBottom: theme.spacing[2],
    },
    featureDescription: {
        fontSize: theme.typography.fontSize.base,
        color: 'var(--text-secondary)',
        lineHeight: 1.6,
        marginBottom: theme.spacing[4],
    },
    benefitsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing[2],
        marginBottom: theme.spacing[6],
    },
    benefitItem: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing[2],
        fontSize: theme.typography.fontSize.sm,
        color: 'var(--text-secondary)',
    },
    featureButton: {
        width: '100%',
        justifyContent: 'center',
        fontWeight: theme.typography.fontWeight.semibold,
    },

    // How It Works Section
    howItWorksSection: {
        padding: `${theme.spacing[16]} ${theme.spacing[6]}`,
        backgroundColor: 'var(--bg-primary)',
    },
    stepsContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: theme.spacing[6],
        maxWidth: '1200px',
        margin: '0 auto',
        flexWrap: 'wrap',
    },
    stepCard: {
        flex: 1,
        minWidth: '250px',
        maxWidth: '300px',
        padding: theme.spacing[8],
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: theme.borderRadius['2xl'],
        textAlign: 'center',
        border: '1px solid var(--border-primary)',
        position: 'relative',
    },
    stepNumber: {
        position: 'absolute',
        top: '-20px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        backgroundColor: '#667eea',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: theme.typography.fontSize.xl,
        fontWeight: theme.typography.fontWeight.bold,
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    },
    stepIcon: {
        marginBottom: theme.spacing[4],
        marginTop: theme.spacing[4],
    },
    stepTitle: {
        fontSize: theme.typography.fontSize.xl,
        fontWeight: theme.typography.fontWeight.bold,
        color: 'var(--text-primary)',
        marginBottom: theme.spacing[3],
    },
    stepDescription: {
        fontSize: theme.typography.fontSize.base,
        color: 'var(--text-secondary)',
        lineHeight: 1.6,
    },
    stepArrow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },

    // CTA Section
    ctaSection: {
        padding: `${theme.spacing[16]} ${theme.spacing[6]}`,
        textAlign: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        margin: `0 ${theme.spacing[6]}`,
        borderRadius: theme.borderRadius['2xl'],
        marginBottom: theme.spacing[8],
    },
    ctaTitle: {
        fontSize: theme.typography.fontSize['4xl'],
        fontWeight: theme.typography.fontWeight.bold,
        marginBottom: theme.spacing[4],
        letterSpacing: '-0.02em',
    },
    ctaSubtitle: {
        fontSize: theme.typography.fontSize.xl,
        opacity: 0.95,
        maxWidth: '600px',
        margin: '0 auto',
        marginBottom: theme.spacing[8],
    },
    ctaButton: {
        backgroundColor: 'white',
        color: '#667eea',
        fontWeight: theme.typography.fontWeight.bold,
    },
};

export default LandingPage;
